// // // /*
// // //     This modal is used to input a pass time

// // //     It should default to the current time when opened

// // //     User should be able to select date dd/mm/yyyy and time HH:MM:SS

// // //     Remove any trailing 'Z' when sending to the server so we don't have UTC time issues
    
// // //     This modal will be used in the LiveTrack edit page from the table

// // //     takes an onclick prop to be able to send to the api for db storage
// // // */
import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useError } from '../../../contexts/ErrorContext';
import { Button } from '../buttons/Button';
import Label from '../labels/Label';
import { useLoading } from '../../../contexts/LoadingContext';
import { getUTCTimeAPI } from '../../../services/utilsAPI';
import { convertToLocalTime, parseLocalTimeInTimezone } from '@utils/dateAndTimeUtils';
import { zoneFor } from '@components/ui/timezone/timezoneMappings';
import { toZonedTime, fromZonedTime, formatInTimeZone } from 'date-fns-tz';

/**
 * Used in LiveTrakEdit to udpate pass times
 * 
 * @param {Boolean} show 
 * @param {Function} handleClose 
 * @param {Function} handleSave 
 * @param {Object} row 
 * @param {Boolean} initialLaunch 
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - UI
 */
const PassTimeSelect = ({ show, handleClose, handleSave, row, initialLaunch = false, timezone }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [initialSpeed, setInitialSpeed] = useState(row?.Speed ? row.Speed : '');
  const [serverTime, setServerTime] = useState(null);

  const {showLoading, hideLoading} = useLoading();

  const { showError } = useError();

  const fetchServerTime = async () => {
    try {
      //gets time from API
      showLoading('Getting Time From Server');
      const response = await getUTCTimeAPI();
      let utcDate = response.data.utctime;
      
      let localTime = convertToLocalTime(utcDate, timezone.value)
      setServerTime(localTime);
    } catch (error) {
      console.log(error)
      showError('Failed to fetch server time');
    } finally {
      hideLoading();
    }
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleTimeChange = (e) => {
    setSelectedTime(e.target.value);
  };

  const handleSpeedChange = (e) => {
    setInitialSpeed(e.target.value);
  };

  const handleNowClick = async() => {
    await fetchServerTime(); 
    if (serverTime) {
      const formattedDate = format(serverTime, 'yyyy-MM-dd');
      const formattedTime = format(serverTime, 'HH:mm:ss');
      setSelectedDate(formattedDate);
      setSelectedTime(formattedTime);
    }
  };

  const handleClearClick = () => {
    setSelectedDate('');
    setSelectedTime('');
    setInitialSpeed('');
  };

  const handleSaveClick = () => {
    let dateTime;
    if (selectedDate === '' && selectedTime === '') {
      dateTime = null;
    } else {
      const localDateTimeString = `${selectedDate}T${selectedTime}`;
      
      dateTime = parseLocalTimeInTimezone(localDateTimeString, timezone)
    }
    
    handleSave(dateTime, initialSpeed, initialLaunch);
    handleClose();
  };

  useEffect(() => {
    if (show) {
      setSelectedDate('');
      setSelectedTime('');
      setInitialSpeed('');
      fetchServerTime();

      if (row && row.PassTime !== null) {
        const dateTime = row.PassTime
        const zone = zoneFor(timezone, dateTime)
        const formattedDate = formatInTimeZone(dateTime, zone, 'yyyy-MM-dd');
        const formattedTime = formatInTimeZone(dateTime, zone, 'HH:mm:ss');
        setSelectedDate(formattedDate);
        setSelectedTime(formattedTime);
        setInitialSpeed(row.Speed)
      }
    }
  }, [row, show]); //eslint-disable-line

  if (!show) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-[21]">
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center">
        <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all max-w-lg w-full max-h-dvh overflow-y-auto">
          <div className="bg-gray-50 py-4 px-6 flex justify-between items-center">
            <div>
              <h5 className="text-lg leading-6 font-bold text-gray-900 mb">{row && row.AGMs[0].Site_Name}</h5>
              <h6 className="text-lmd leading-6 font-medium text-gray-900">Select Date and Time</h6>
            </div>
            <button type="button" className="text-gray-400 hover:text-gray-500" onClick={handleClose}>
              &times;
            </button>
          </div>

          <div className="py-4 px-6">
            {initialLaunch && (
              <div className="mb-4">
                <Label>Initial Speed (km/h)</Label>
                <input
                  type="number"
                  value={initialSpeed}
                  onChange={handleSpeedChange}
                  className="mt-1 p-2 block w-full rounded-md border border-gray-200 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
              </div>
            )}
            <h1 className="italic text-red-500">*Time and Date Displayed in <span className="font-bold underline">{timezone.label}</span></h1>
            <div className="mb-4">
              <Label>Date</Label>
              <input
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                className="mt-1 block w-full rounded-md border border-gray-200 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              />
            </div>

            <div className="mb-4">
              <Label>Time</Label>
              <input
                type="time"
                value={selectedTime}
                onChange={handleTimeChange}
                step="1"
                className="mt-1 block w-full rounded-md border border-gray-200 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              />
            </div>

            <Button
              className="mr-2 mb-2 mt-2"
              variant="primary"
              onClick={handleNowClick}
            >
              Now
            </Button>

            <Button
              variant="danger"
              onClick={handleClearClick}
            >
              Clear
            </Button>
          </div>

          <div className="bg-gray-50 p-4 flex justify-end">
            <Button
              className="mb-2 mt-2"
              variant="cancel"
              onClick={handleClose}
            >
              Close
            </Button>

            <Button
              className="ml-2 mb-2 mt-2"
              variant="success"
              onClick={handleSaveClick}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PassTimeSelect;
